import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import axios, { type AxiosResponse } from 'axios'
import { formatDate } from 'commons/utils/date'
import { TypesValidations } from 'constants/enums'
import { toast } from 'react-toastify'
import api from 'services/api'
import { getMaterialStock } from 'services/product'
import type { MaterialStock } from 'services/product/interface'
import { fetchMaterialShare } from 'store/FairShareContext/fairShareSlice'

import type { AppThunk } from '../store'
import type {
  CenterMaterial,
  Color,
  IMaterialCorByOv,
  MaterialByIdQuery,
  MaterialByIdResponse,
  MaterialColorByIdResponse,
} from './productContextTypes'

interface MaterialsByColor {
  cd_cor: string
  tamanhos: MaterialColorByIdResponse[]
}

type InitialState = {
  loadingProduct: boolean
  materialSelected: MaterialByIdResponse
  filter: string[]
  materialUrlSelected: string
  materialsByColor: MaterialsByColor[]
  defaultParams: MaterialByIdQuery
  centerMaterial: CenterMaterial[]
  matrixStock: MaterialStock
  loadingStock: boolean
  hasMoreColors: boolean
}
const initialState: InitialState = {
  centerMaterial: [] as CenterMaterial[],
  defaultParams: {} as MaterialByIdQuery,
  filter: [] as string[],
  hasMoreColors: true,
  loadingProduct: true,
  loadingStock: true,
  materialSelected: {} as MaterialByIdResponse,
  materialsByColor: [] as MaterialsByColor[],
  materialUrlSelected: '',
  matrixStock: {} as MaterialStock,
}
const numeroCoresPorPagina = 25

const products = createSlice({
  initialState,
  name: 'products',
  reducers: {
    afterRequest(state: InitialState) {
      state.loadingProduct = false
    },
    clearMaterialSelected(state: InitialState) {
      state.materialSelected = {} as MaterialByIdResponse
      state.materialUrlSelected = ''
      state.centerMaterial = [] as CenterMaterial[]
      const param = { ...state.defaultParams }
      delete param.cd_matr
      state.defaultParams = param
      state.filter = []
      state.hasMoreColors = true
    },
    clearMatrixStock(state: InitialState) {
      state.matrixStock = {} as MaterialStock
    },
    incrementToDefaultParams(state: InitialState, action: PayloadAction<string>) {
      state.defaultParams = { ...state.defaultParams, cd_matr: action.payload }
    },
    onRequest(state: InitialState) {
      state.loadingProduct = true
    },
    setDefaultParams(state: InitialState, action: PayloadAction<MaterialByIdQuery>) {
      state.defaultParams = action.payload
    },
    setFalseHasMore(state: InitialState) {
      state.hasMoreColors = false
    },
    setLoadingStock(state: InitialState, action: PayloadAction<boolean>) {
      state.loadingStock = action.payload
    },
    setMaterialFilter(state: InitialState, action: PayloadAction<string[]>) {
      state.filter = action.payload
    },
    setMaterialSelected(state: InitialState, action: PayloadAction<MaterialByIdResponse>) {
      const cd_matr = action.payload.cd_matr
      state.materialSelected = action.payload
      state.defaultParams = { ...state.defaultParams, cd_matr: cd_matr }
      state.materialUrlSelected = action.payload.ds_matr
        .toLocaleLowerCase()
        .replace('- ', '')
        .replace(/ /g, '-')
        .replace(/(\.|\/)/g, '')
      state.centerMaterial = action.payload?.referencias?.map((item, index) => ({
        title: item.cd_matr_refe,
        value: index.toString(),
      }))
    },
    setMaterialsByColor(state: InitialState, action: PayloadAction<MaterialsByColor[]>) {
      state.materialsByColor = action.payload
    },
    setMatrixStock(state: InitialState, action: PayloadAction<MaterialStock>) {
      state.matrixStock = action.payload
    },
    updateMaterialSelected(state: InitialState, { payload }: PayloadAction<Color[]>) {
      state.materialSelected = {
        ...state.materialSelected,
        cores: [...state.materialSelected.cores, ...payload],
      }
    },
  },
})

export const productContext = products.reducer
export const {
  clearMaterialSelected,
  setMaterialSelected,
  onRequest,
  setMaterialsByColor,
  setMaterialFilter,
  setDefaultParams,
  incrementToDefaultParams,
  afterRequest,
  updateMaterialSelected,
  setMatrixStock,
  setLoadingStock,
  clearMatrixStock,
  setFalseHasMore,
} = products.actions

export function asyncFilterByCode(code: string): AppThunk {
  return async function (dispatch, getState) {
    const { defaultParams } = getState().products
    const query = {
      ...defaultParams,
      cd_matr: code,
    }
    dispatch(asyncGetMaterialById(query))
  }
}

export function asyncGetMaterialCorByOv(
  query: IMaterialCorByOv,
  errorCallback?: () => void,
): AppThunk {
  return async function (dispatch) {
    dispatch(onRequest())
    api
      .get(`/produto/v2/MaterialCorByOv`, {
        params: {
          ...query,
          cd_cor: query.cd_cor ?? '',
          nr_item_orde_vend: query.nr_item_orde_vend ?? '',
        },
      })
      .then((response: AxiosResponse<MaterialByIdResponse>) => {
        const res = { ...response.data }
        const cores = res.cores.map((cor) => ({
          ...cor,
          checked: false,
          closed: false,
          ds_cor: cor.ds_cor.replace('.', ' '),
        }))
        if (cores.length < numeroCoresPorPagina) {
          dispatch(setFalseHasMore())
        }

        if (Number(query.nume_pagi) === 1) {
          dispatch(
            setMaterialSelected({
              ...response.data,
              cores: cores,
              res_cent_matr: response.data.cd_cent_matr || '',
            }),
          )
        } else {
          dispatch(updateMaterialSelected(cores))
        }
        dispatch(afterRequest())
      })
      .catch(() => errorCallback && errorCallback())
  }
}

export function asyncGetMaterialById(
  query: MaterialByIdQuery,
  numeroPagina = '1',
  signal?: AbortSignal,
): AppThunk {
  return async function (dispatch) {
    dispatch(onRequest())
    dispatch(getProductStock(query, signal))
    if (query.cd_matr && query.cd_cent_matr) {
      dispatch(fetchMaterialShare(query.cd_matr, query.cd_cent_matr))
    }

    api
      .get(`/produto/v2/MaterialPedidoById`, {
        params: {
          ...query,
          cd_cent_matr: query?.cd_cent_matr ?? '',
          cd_cor: '',
          dt_dsjd_pedi: '',
          fl_matr_2qua: 'N',
          nr_item_pedi_rasc: query.nr_item_pedi_rasc ?? '',
          nume_pagi: numeroPagina,
          nume_regi_pagi: numeroCoresPorPagina.toString(),
        },
      })
      .then((response: AxiosResponse<MaterialByIdResponse>) => {
        const res = { ...response.data }
        const cores = res.cores.map((cor) => ({
          ...cor,
          checked: false,
          closed: false,
          ds_cor: cor.ds_cor.replace('.', ' '),
        }))
        if (cores.length < numeroCoresPorPagina) {
          dispatch(setFalseHasMore())
        }

        if (Number(numeroPagina) === 1) {
          dispatch(
            setMaterialSelected({
              ...response.data,
              cd_cent_matr: query?.cd_cent_matr || '',
              cores: cores,
              nr_item_pedi_rasc: query?.nr_item_pedi_rasc ?? undefined,
              res_cent_matr: response.data.cd_cent_matr || '',
            }),
          )
        } else {
          dispatch(updateMaterialSelected(cores))
        }
      })
      .catch((err) => {
        console.error('Error =>', err)
      })
      .finally(() => {
        dispatch(afterRequest())
      })
  }
}

export const getProductStock =
  (query: MaterialByIdQuery, signal?: AbortSignal): AppThunk =>
  async (dispatch) => {
    dispatch(clearMatrixStock())
    dispatch(setLoadingStock(true))
    try {
      const payload = {
        canal: query.cd_canl_dstb,
        centro: query.cd_cent_matr,
        dataFaturamento: formatDate(query.dt_dsjd_pedi, 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD'),
        empresa: query.cd_empr,
        escritorio: query.cd_escr_vend,
        material: query.cd_matr,
        materialEmLinha: query.fl_matr_fora_linh === TypesValidations.NO,
        organizacao: query.cd_orgn_vend,
        preparacaoMto: query.fl_mto_prep === TypesValidations.YES,
        setor: query.cd_seto_atvd,
      }

      const stockData = await getMaterialStock(payload, signal)
      if (stockData) {
        dispatch(setMatrixStock(stockData))
      }
      dispatch(setLoadingStock(false))
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error('Erro ao carregar dados do material.')
        dispatch(setLoadingStock(false))
      }
    }
  }
